import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { useCapacity, useRelay } from "../api/queries";
import { useSession } from "../auth/SessionProvider";
import type { GuideNumbers, GuideSection } from "../content/guide";
import { GUIDE, nextSection, previousSection } from "../content/guide";

/** How far under the viewport's top a subhead counts as "being read". */
const READING_LINE_PX = 100;

/** One subhead of the current section, as the right-hand rail lists it. */
interface Subhead {
  readonly id: string;
  readonly label: string;
}

/**
 * What the rail offers when a section has no subheads of its own: the next
 * few sections in reading order, wrapping past the end — so the column is
 * never an empty strip of screen.
 */
function keepReading(currentId: string): readonly GuideSection[] {
  const index = GUIDE.findIndex((section) => section.id === currentId);
  const after = GUIDE.slice(index + 1);
  const before = GUIDE.slice(0, index);
  return [...after, ...before].slice(0, 4);
}

/**
 * One section of the guide, as its own page.
 *
 * Content comes from `content/guide.tsx`; this page contributes the frame
 * (heading, prose column, the previous/next pager) and the numbers. Figures
 * come from `GET /api/v1/capacity` through `GuideNumbers`, and whether
 * warnings also reach Slack from `GET /api/v1/me` — never typed into a
 * sentence, so the guide cannot drift from the deployment it describes.
 *
 * One deliberate departure from the console screens: no error ladder. When
 * capacity cannot be read, the section still renders in full and its
 * sentences degrade to words without figures — the moment the cluster is
 * unreachable is exactly the moment somebody opens the manual, and a manual
 * that vanished then would be failing at its own job.
 *
 * Returns:
 *     The section, or a redirect to the index for an unknown id.
 */
export function DocsSectionPage(): JSX.Element {
  const { sectionId } = useParams();
  const { authConfig, me } = useSession();
  const capacity = useCapacity();
  const relay = useRelay();

  const section = GUIDE.find((candidate) => candidate.id === sectionId);

  // Arriving through the pager keeps the old scroll position otherwise, and
  // a new chapter that opens at its middle reads as a broken link.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sectionId]);

  // The rail on the right is derived from the rendered subheads: the content
  // declares no table of itself, so the two can never disagree. Ids are
  // stamped here (the anchors need them) and restamped when late data
  // re-renders the body. The active entry follows the reading line.
  const [subheads, setSubheads] = useState<readonly Subhead[]>([]);
  const [activeSubhead, setActiveSubhead] = useState<string>("");

  useEffect(() => {
    const headings = Array.from(
      document.querySelectorAll<HTMLHeadingElement>(".docs-prose h2"),
    );
    const items = headings.map((heading) => {
      const label = heading.textContent ?? "";
      const id =
        heading.id ||
        label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      heading.id = id;
      return { id, label };
    });
    setSubheads(items);

    const onScroll = (): void => {
      let current = items[0]?.id ?? "";
      for (const item of items) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= READING_LINE_PX) {
          current = item.id;
        }
      }
      setActiveSubhead(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sectionId, capacity.data, relay.data]);

  if (!section) {
    return <Navigate to="/docs" replace />;
  }

  const numbers: GuideNumbers = {
    gpu: capacity.data?.defaults ?? null,
    cpu: capacity.data?.cpu?.defaults ?? null,
    bootEtaMinutes: capacity.data?.node_start_eta_minutes ?? null,
    relay: relay.data ?? null,
    contact: authConfig.access_contact || null,
    // Where a warning arrives is the guide's, and the account's answer is the
    // only one that knows: this deployment either has a Slack app or it does
    // not, and the sentence follows it rather than guessing.
    slackNotifications: me.slack_notifications,
  };

  const previous = previousSection(section.id);
  const next = nextSection(section.id);

  return (
    <div className="docs-section">
      <article className="docs-prose">
        <header className="docs-head">
          <span className="eyebrow">{section.group}</span>
          <h1 className="docs-head__title">{section.title}</h1>
          <p className="docs-head__lead">{section.lead}</p>
        </header>

        {section.body(numbers)}

        <nav className="docs-pager" aria-label="Neighbouring sections">
          {previous ? (
            <Link to={`/docs/${previous.id}`} className="docs-pager__link">
              <IconArrowLeft size={14} aria-hidden="true" />
              <span className="docs-pager__text">
                <span className="eyebrow">Previous</span>
                {previous.title}
              </span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              to={`/docs/${next.id}`}
              className="docs-pager__link docs-pager__link--next"
            >
              <span className="docs-pager__text">
                <span className="eyebrow">Next</span>
                {next.title}
              </span>
              <IconArrowRight size={14} aria-hidden="true" />
            </Link>
          ) : null}
        </nav>
      </article>

      {subheads.length >= 2 ? (
        <nav className="docs-onpage" aria-label="On this page">
          <span className="eyebrow docs-onpage__title">On this page</span>
          {subheads.map((subhead) => (
            <a
              key={subhead.id}
              href={`#${subhead.id}`}
              className="docs-onpage__link"
              aria-current={activeSubhead === subhead.id ? "true" : undefined}
            >
              {subhead.label}
            </a>
          ))}
        </nav>
      ) : (
        <nav className="docs-onpage" aria-label="Keep reading">
          <span className="eyebrow docs-onpage__title">Keep reading</span>
          {keepReading(section.id).map((candidate) => (
            <Link
              key={candidate.id}
              to={`/docs/${candidate.id}`}
              className="docs-onpage__link"
            >
              {candidate.title}
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}
